import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import {Link, useParams} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {useGetTranslations} from '../../utils/hooks/useGetTranslations';
import {TranslationKeys} from '../../utils/constants/routeConstants';
import {articleAsyncActions, getTeiArticle} from '../../redux/pageAsyncActions';
import {closeArticle} from '../../redux/article/actions';
import Loader from '../../components/loader';
import {doiUrl} from '../../utils/constants/constants';
import PdfDocument from 'assets/image/article/pdf-document.svg';
import PdfDocumentGray from 'assets/image/article/pdf-document-gray.svg';
import {getArticlePdfApi} from '../../redux/api/article';

import './styles.scss';

export const ArticlePage: React.FunctionComponent = () => {
  const {
    article: {
      article
    },
    status: {
      loading
    }
  } = useSelector((state: IStore) => state);
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const {articleId} = useParams();
  const [keywords, setKeywords] = useState<string[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  useGetTranslations({
    translationKey: TranslationKeys.Article
  });

  useEffect(() => {
    if (articleId) {
      if (article && article.id.toString() !== articleId) {
        dispatch(closeArticle());
      }
      dispatch(articleAsyncActions[getTeiArticle](articleId));
    }
  }, [articleId]);

  useEffect(() => {
    if (article && article.keywords) {
      setKeywords(article.keywords.split(/(?:,| |;|\n)+/));
    }
  }, [article]);

  const handleDownload = async () => {
    if (!article) {
      return;
    }
    try {
      setPdfLoading(true);
      const {data: {value: byteArray} } = await getArticlePdfApi(article.id);

      if (!byteArray) {
        return;
      }
      const byteCharacters = atob(byteArray);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const bytes = new Uint8Array(byteNumbers);

      const blob = new Blob([bytes], {type: 'application/pdf'});
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;
      link.download = article?.fileName || 'file.pdf';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  return <div>
    {!article && loading.includes(getTeiArticle) && <Loader className="article-page__loader"/>}
    {article && <div className="article-page">
      <header className="article-page-header">
        <h1 className="article-page-header__title">{article.title}</h1>
        <p className="article-page-header__authors article-page-header__line">
          <Link className="article-page-header__link" to={`/article/authors/${article.id}`}>{article.authors}</Link>
        </p>
        {article.date && <p className={'article-page-header__line'}>
          <FormattedMessage
            id={'gritx.article.firstPublished'}
            defaultMessage={'First published'}
          />: {article.date}
        </p>}
        <div className="article-page-header__line article-page-header__actions">
          {article.fileName && <button disabled={pdfLoading} onClick={handleDownload} className="article-page-header__pdf-btn"
            title={formatMessage({
              id: 'gritx.article.downloadPdf',
              defaultMessage: 'Download PDF'
            })}>
            <img src={pdfLoading ? PdfDocumentGray : PdfDocument} alt="" className="article-page-header__pdf-img"/>
          </button>}
          {article.doi && <span className="doi">
            <FormattedMessage
              id={'gritx.article.DOI'}
              defaultMessage={'DOI'}
            />: <a href={`${doiUrl}${article.doi}`} className="article-page-header__link" target="_blank"
              rel="noreferrer">{doiUrl}{article.doi}</a>
          </span>}
        </div>
      </header>
      <main className="article-page-container">
        <section className="article-page-container__summary-card">
          <h2><FormattedMessage
            id={'gritx.article.summary'}
            defaultMessage={'Summary'}
          /></h2>
          <p>{article.summary}</p>
          <div className="article-page-container__keywords">
            <strong><FormattedMessage
              id={'gritx.article.keywords'}
              defaultMessage={'Keywords'}
            />: </strong>
            {keywords.map((keyword, i) => {
              return <span key={i} className="article-page-container__chip">{keyword}</span>;
            })}
          </div>
        </section>
        <section className="article-page-container__meta-links">
          <div>
            <FormattedMessage
              id={'gritx.article.references'}
              defaultMessage={'References'}
            />: <Link className="article-page-container__link"
              to={`/article/references/${article.id}`}>{article.referenceCount}</Link>
          </div>
          <div>
            <FormattedMessage
              id={'gritx.article.collections'}
              defaultMessage={'Collections'}
            />: <Link className="article-page-container__link"
              to={`/article/collections/${article.id}`}>{article.collectionCount}</Link>
          </div>
        </section>
        <div className="article-page-container__card-grid">
          <div className="article-page-container__card">
            <h3><FormattedMessage
              id={'gritx.article.sections'}
              defaultMessage={'Sections'}
            /></h3>
            <p>{article.sections?.length || 0} <FormattedMessage
              id={'gritx.article.sections'}
              defaultMessage={'Sections'}
            /></p>
            <Link to={`/article/tableofcontents/${article.id}`} className="article-page-container__link">
              <FormattedMessage
                id={'gritx.article.viewSections'}
                defaultMessage={'View Sections'}
              />
            </Link>
          </div>
          <div className="article-page-container__card">
            <h3><FormattedMessage
              id={'gritx.article.generatedSummary'}
              defaultMessage={'Generated summary'}
            /></h3>
            <Link to={`/article/summary/${article.id}`} className="article-page-container__link">
              <FormattedMessage
                id={'gritx.article.viewSummary'}
                defaultMessage={'View Summary'}
              />
            </Link>
          </div>
          <div className="article-page-container__card">
            <h3><FormattedMessage
              id={'gritx.article.tables'}
              defaultMessage={'Tables'}
            /></h3>
            <p>{article.tableCount} <FormattedMessage
              id={'gritx.article.tables'}
              defaultMessage={'Tables'}
            /></p>
            <Link to={`/article/tables/${article.id}`} className="article-page-container__link">
              <FormattedMessage
                id={'gritx.article.viewTables'}
                defaultMessage={'View Tables'}
              />
            </Link>
          </div>
        </div>
      </main>
    </div>}
  </div>;
};
